import { useCallback, useEffect, useMemo } from 'react';
import {
  Chat,
  Loading,
  LineAddFriendModal,
  PageWrapper,
  Text
} from 'components';

import { useQuery } from '@tanstack/react-query';
import {
  getChannelDetail,
  getNewLineChannelToAddFriend
} from 'api';
import { useUserStore } from 'store';
import {
  TEXT_STRING,
  queryKeys,
  LINE_ADD_FRIEND_BASE_URL,
  storageKeys
} from '../../constants';
import { registerTrustedRecallPublisher } from '../../utils/chatRecallMessage';

function Talk() {
  // Hook
  const isAuthenticated = useUserStore(state => state.isAuthenticated);
  const user = useUserStore(state => state.user);
  const clientId = useUserStore(state => state.clientId);
  const mgmChannelId = useUserStore(state => state.mgmChannelId);

  // Use mgmChannelId from store for management chat
  const channelId = mgmChannelId || '';
  const accessToken = localStorage.getItem(storageKeys.ACCESS_TOKEN);

  // Query
  const {
    data,
    isLoading: loadingChannelDetail,
    refetch,
    isError,
  } = useQuery({
    queryKey: [queryKeys.CHANNEL_DETAIL, channelId, accessToken],
    queryFn: () => getChannelDetail(channelId),
    enabled: !!isAuthenticated && !!channelId && !!channelId.trim(),
    retry: 0,
    retryOnMount: false,
    refetchOnWindowFocus: false,
  });
  const channelData = useMemo(
    () => data,
    [data]
  );

  useEffect(() => {
    if (!channelData?.pubnubSystemBackendId) return;

    registerTrustedRecallPublisher(channelData.pubnubSystemBackendId);
    localStorage.setItem(
      storageKeys.PUBNUB_SYSTEM_BACKEND_ID,
      channelData.pubnubSystemBackendId,
    );
  }, [channelData?.pubnubSystemBackendId]);

  const channelIdForApi = useMemo(() => channelData?.id ?? channelId,
    [channelData?.id, channelId]);

  const { data: newBasicId } = useQuery({
    queryKey: [queryKeys.LINE_CHANNEL_STATUS, channelIdForApi],
    queryFn: () => getNewLineChannelToAddFriend(channelIdForApi),
    enabled: !!isAuthenticated && !!channelIdForApi && !!channelData,
    retry: 0,
    refetchOnWindowFocus: false,
  });

  const showLineAddModal = !!newBasicId;

  const lineAddFriendUrl =
    useMemo(() => newBasicId
      ? `${LINE_ADD_FRIEND_BASE_URL}${newBasicId}`
      : '#', [newBasicId]);

  const channelParticipants = useMemo(
    () =>
      channelData?.participants?.filter(
        (member) => ![user?.uuid, clientId].includes(member.uuid)
      ),
    [channelData?.participants, user?.uuid, clientId]
  );

  const onFocusInput = useCallback(async () => {
    // No longer needed
  }, []);

  const onBlurInput = useCallback(async () => {
    // No longer needed
  }, []);

  const handleRefetch = useCallback(() => {
    refetch();
  }, [refetch]);


  return (
    <>
      <LineAddFriendModal
        isOpen={showLineAddModal}
        lineAddFriendUrl={lineAddFriendUrl}
      />
      <PageWrapper
        key={channelId}
        className="flex flex-col justify-between items-center w-full h-full text-sm"
      >
        {loadingChannelDetail ? (
          <div className="h-full flex items-center">
            <Loading />
          </div>
        ) : (
          <>
            {!!channelData && !isError ? (
              <>
                <div className="flex-1 overflow-hidden w-full h-full translate-x-0 translate-y-0">
                  <Chat
                    channel={channelId}
                    channelData={channelData}
                    users={channelParticipants}
                    onBlur={onBlurInput}
                    onFocus={onFocusInput}
                    refetch={handleRefetch}
                    refetchChannelDetail={refetch}
                  />
                </div>
              </>
            ) : (
              <div className="h-full w-full flex flex-col justify-between items-center">
                <Text center bold fontSize={16}>
                  {TEXT_STRING.COMMON.MESSAGE_REMOVED_CHANNEL}
                </Text>
                <div></div>
              </div>
            )}
          </>
        )}
      </PageWrapper>
    </>
  );
}

export default Talk;
